import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as Sentry from "@sentry/react";
import { PageHeader } from './page-header';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Slider } from './ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from './ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Upload, Check, Loader2, Info, ExternalLink } from 'lucide-react';
import { useSettings } from '../hooks/useSettings';
import { useSubscription } from '../hooks/useSubscription';
import { PricingDialog } from './pricing-dialog';
import { DeleteAccountDialog } from './delete-account-dialog';
import { WatermarkPreview } from './watermark-preview';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase-client';

interface SettingsProps {
  onBack?: () => void;
  onOpenAdmin?: () => void;
}

export function Settings({ onBack, onOpenAdmin }: SettingsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile, settings, loading, updateProfile, updateSettings, uploadFile, updatePassword, refreshSettings } = useSettings();
  const { subscription, creditsAvailable, creditsLimit, planDisplayName, priceDisplay, isActive, invoices } = useSubscription();
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Local state for buffered inputs
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Billing - Invoice Details
  const [billingCompany, setBillingCompany] = useState('');
  const [billingNip, setBillingNip] = useState('');
  // Billing - Address
  const [billingStreet, setBillingStreet] = useState('');
  const [billingPostalCode, setBillingPostalCode] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingCountry, setBillingCountry] = useState('PL');

  const [emailFooter, setEmailFooter] = useState('');

  // Branding - Watermark Local State (for instant preview)
  const [watermarkEnabled, setWatermarkEnabled] = useState(false);
  const [watermarkOpacity, setWatermarkOpacity] = useState(80); // 0-100
  const [watermarkScale, setWatermarkScale] = useState(100); // 50-200
  const [watermarkPosition, setWatermarkPosition] = useState<string>('bottom-right');

  const [wyskoWatermarkEnabled, setWyskoWatermarkEnabled] = useState(false);
  const [wyskoWatermarkPosition, setWyskoWatermarkPosition] = useState<string>('bottom-left');
  const [wyskoWatermarkOpacity, setWyskoWatermarkOpacity] = useState(30); // 0-100
  const [wyskoWatermarkScale, setWyskoWatermarkScale] = useState(100); // 50-200

  // Sample image and Wysko logo for preview
  const [sampleImageUrl, setSampleImageUrl] = useState<string | null>(null);
  const [isLoadingSample, setIsLoadingSample] = useState(true);
  const [wyskoLogoUrl, setWyskoLogoUrl] = useState<string | null>(null);

  // Props for password dialog
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Delete account dialog
  const [isDeleteAccountOpen, setIsDeleteAccountOpen] = useState(false);

  // Flag to prevent re-sync after initial load
  const [settingsInitialized, setSettingsInitialized] = useState(false);

  // Sync local state with loaded data
  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
    }
  }, [profile]);

  useEffect(() => {
    // Sync only once on initial load to prevent overwriting user edits
    if (settings && !settingsInitialized) {
      setSettingsInitialized(true);

      // Billing
      setBillingCompany(settings.billing_company_name || settings.invoice_company_name || '');
      setBillingNip(settings.billing_tax_id || settings.invoice_nip || '');
      // Address (parse old address string if new fields empty?)
      // For now prioritize new fields
      setBillingStreet(settings.billing_street_address || '');
      setBillingPostalCode(settings.billing_postal_code || '');
      setBillingCity(settings.billing_city || '');
      setBillingCountry(settings.billing_country || 'PL');

      setEmailFooter(settings.email_footer || '');

      // Branding
      setWatermarkEnabled(settings.watermark_enabled);
      setWatermarkOpacity(typeof settings.watermark_opacity === 'number' ? settings.watermark_opacity * 100 : 80);
      setWatermarkScale(typeof settings.watermark_scale === 'number' ? settings.watermark_scale * 100 : 100);
      setWatermarkPosition(settings.watermark_position || 'bottom-right');

      setWyskoWatermarkEnabled(settings.wysko_watermark_enabled || false);
      setWyskoWatermarkPosition(settings.wysko_watermark_position || 'bottom-left');
      setWyskoWatermarkOpacity(Math.round((settings.wysko_watermark_opacity || 0.3) * 100));
      setWyskoWatermarkScale(settings.wysko_watermark_scale || 100);
    }
  }, [settings, settingsInitialized]);

  // Auto-open pricing dialog when deep-linked from email (e.g. ?tab=pricing)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'pricing') {
      setIsPricingOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, []);

  // Fetch sample image and Wysko logo for preview
  useEffect(() => {
    const loadSampleAndLogo = async () => {
      if (!profile?.id) return; // Guard against undefined profile.id
      setIsLoadingSample(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setIsLoadingSample(false);
        return;
      }

      // 1. Fetch user's last generated variant
      const { data: lastImage } = await supabase
        .from('images')
        .select('gcs_path')
        .eq('user_id', profile.id)
        .eq('image_type', 'generated')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // 2. Prepare items for signing
      const itemsToSign = [
        { bucket: 'wysko-assets', path: 'branding/wysko-watermark.png' }
      ];

      if (lastImage?.gcs_path) {
        itemsToSign.push({ bucket: 'wysko-generated', path: lastImage.gcs_path });
      } else {
        // Fallback: admin sample image
        itemsToSign.push({ bucket: 'wysko-assets', path: 'branding/watermark-sample.jpg' });
      }

      // 3. Sign URLs
      try {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/server/gcs/sign-urls`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ items: itemsToSign })
        });

        if (response.ok) {
          const result = await response.json();
          setWyskoLogoUrl(result.data?.['branding/wysko-watermark.png'] || null);

          if (lastImage?.gcs_path) {
            setSampleImageUrl(result.data?.[lastImage.gcs_path] || null);
          } else {
            setSampleImageUrl(result.data?.['branding/watermark-sample.jpg'] || null);
          }
        }
      } catch (error) {
        console.error('Failed to load sample image and logo:', error);
      }

      setIsLoadingSample(false);
    };

    loadSampleAndLogo();
  }, [profile]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadingAvatar(true);
      const url = await uploadFile(e.target.files[0], 'avatars');
      if (url) {
        await updateProfile({ avatar_url: url });
      }
      setUploadingAvatar(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Basic validation
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Plik jest za duży (max 2MB)');
        return;
      }
      if (!['image/png', 'image/jpeg'].includes(file.type)) {
        toast.error('Dozwolone są tylko pliki PNG i JPG');
        return;
      }

      setUploadingLogo(true);
      const path = await uploadFile(file, 'logos');
      if (path) {
        // If path returned (private), we might need to refresh settings to get signed URL for display.
        // But useSettings handles this in loadSettings.
        // We trigger an update with the PATH.
        await updateSettings({ watermark_logo_url: path });
        // Trigger reload to get signed URL for preview
        if (refreshSettings) await refreshSettings();

        toast.success("Logo wgrane pomyślnie.");
      }
      setUploadingLogo(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Hasła nie są identyczne');
      return;
    }
    await updatePassword(newPassword);
    setIsPasswordOpen(false);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSaveAll = async () => {
    // Save all buffered text inputs & sliders
    try {
      console.log('[Settings] Saving watermark settings:', {
        watermark_enabled: watermarkEnabled,
        watermark_opacity: watermarkOpacity / 100,
        watermark_scale: watermarkScale / 100,
        watermark_position: watermarkPosition,
        wysko_watermark_enabled: wyskoWatermarkEnabled,
        wysko_watermark_position: wyskoWatermarkPosition,
        wysko_watermark_opacity: wyskoWatermarkOpacity / 100,
        wysko_watermark_scale: wyskoWatermarkScale,
      });

      await Promise.all([
        updateProfile({
          first_name: firstName,
          last_name: lastName
        }),
        updateSettings({
          billing_company_name: billingCompany,
          billing_tax_id: billingNip,
          billing_street_address: billingStreet,
          billing_postal_code: billingPostalCode,
          billing_city: billingCity,
          billing_country: billingCountry,

          email_footer: emailFooter,

          watermark_enabled: watermarkEnabled,
          watermark_opacity: watermarkOpacity / 100,
          watermark_scale: watermarkScale / 100,
          watermark_position: watermarkPosition as any,

          wysko_watermark_enabled: wyskoWatermarkEnabled,
          wysko_watermark_position: wyskoWatermarkPosition as any,
          wysko_watermark_opacity: wyskoWatermarkOpacity / 100,
          wysko_watermark_scale: wyskoWatermarkScale,
        })
      ]);
      toast.success('Ustawienia znaku wodnego zostały zapisane');
    } catch (error) {
      console.error('[Settings] Save failed:', error);
      toast.error('Nie udało się zapisać ustawień');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-full relative isolate">
      <PageHeader
        title="Ustawienia"
      />

      <div className="px-10 py-10 max-w-5xl">
        {/* Profil i Konto */}
        <section className="mb-12">
          <h2 className="text-[15px] font-medium mb-6 pb-3 border-b border-border">
            Profil i Konto
          </h2>

          <div className="space-y-6">
            {/* Avatar */}
            <div className="flex items-start gap-6">
              <Avatar className="w-20 h-20 border border-border">
                <AvatarImage src={profile?.avatar_url || ''} />
                <AvatarFallback className="bg-primary text-primary-foreground text-[18px] font-medium">
                  {profile?.first_name?.[0]}{profile?.last_name?.[0] || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <Label className="text-[13px] mb-2 block">Zdjęcie profilowe</Label>
                <div className="relative">
                  <Input
                    type="file"
                    className="hidden"
                    id="avatar-upload"
                    accept="image/png, image/jpeg"
                    onChange={handleAvatarUpload}
                    disabled={uploadingAvatar}
                  />
                  <Button variant="outline" size="sm" className="gap-2" asChild>
                    <label htmlFor="avatar-upload" className="cursor-pointer">
                      {uploadingAvatar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" strokeWidth={2} />}
                      {uploadingAvatar ? 'Przesyłanie...' : 'Zmień zdjęcie'}
                    </label>
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  JPG lub PNG, maksymalnie 2MB
                </p>
              </div>
            </div>

            {/* Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" className="text-[13px] mb-2 block">
                  Imię
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="h-9 text-[13px]"
                />
              </div>
              <div>
                <Label htmlFor="lastName" className="text-[13px] mb-2 block">
                  Nazwisko
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="h-9 text-[13px]"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-[13px] mb-2 block">
                Adres e-mail
              </Label>
              <Input
                id="email"
                type="email"
                value={profile?.email || ''}
                readOnly
                disabled
                className="h-9 text-[13px] max-w-md bg-muted/50"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Adres e-mail służy do logowania i nie może zostać zmieniony.
              </p>
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password" className="text-[13px] mb-2 block">
                Hasło
              </Label>
              <Dialog open={isPasswordOpen} onOpenChange={setIsPasswordOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    Zmień hasło
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Zmiana hasła</DialogTitle>
                    <DialogDescription>
                      Wprowadź nowe hasło poniżej.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-password">Nowe hasło</Label>
                      <Input
                        id="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Potwierdź hasło</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsPasswordOpen(false)}>Anuluj</Button>
                    <Button onClick={handlePasswordUpdate}>Zmień hasło</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* Usuń konto - na końcu sekcji Profil i Konto */}
            <div className="pt-6 mt-6 border-t border-border">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-[13px] text-destructive">Usuń konto</Label>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Trwale usuwa Twoje konto i wszystkie dane
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive border-destructive/50 hover:bg-destructive/10"
                  onClick={() => setIsDeleteAccountOpen(true)}
                >
                  Usuń konto
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Powiadomienia */}
        <section className="mb-12">
          <h2 className="text-[15px] font-medium mb-6 pb-3 border-b border-border">
            Powiadomienia
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-3">
              <div>
                <Label htmlFor="email-notif" className="text-[13px] cursor-pointer">
                  Powiadomienia e-mail
                </Label>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Otrzymuj powiadomienia o zakończonych renderach
                </p>
              </div>
              <Switch
                id="email-notif"
                checked={settings?.email_notifications}
                onCheckedChange={(checked) => updateSettings({ email_notifications: checked })}
              />
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <Label htmlFor="render-notif" className="text-[13px] cursor-pointer">
                  Powiadomienia w aplikacji
                </Label>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Wyświetlaj powiadomienia podczas korzystania z aplikacji
                </p>
              </div>
              <Switch
                id="render-notif"
                checked={settings?.app_notifications}
                onCheckedChange={(checked) => updateSettings({ app_notifications: checked })}
              />
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <Label htmlFor="newsletter" className="text-[13px] cursor-pointer">
                  Newsletter
                </Label>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Otrzymuj porady, nowości i aktualizacje produktu
                </p>
              </div>
              <Switch
                id="newsletter"
                checked={settings?.newsletter_subscribed ?? false}
                onCheckedChange={async (checked) => {
                  await updateSettings({ newsletter_subscribed: checked });
                  // Sync with Resend Audience
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (session?.access_token) {
                      fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/audience-sync/sync`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${session.access_token}`,
                        },
                        body: JSON.stringify({ subscribe: checked }),
                      }).catch(err => console.error('[Newsletter] Audience sync failed:', err));
                    }
                  } catch (err) {
                    console.error('[Newsletter] Audience sync error:', err);
                  }
                }}
              />
            </div>
          </div>
        </section>

        {/* Subskrypcja i Płatności */}
        <section className="mb-12">
          <h2 className="text-[15px] font-medium mb-6 pb-3 border-b border-border">
            Subskrypcja i Płatności
          </h2>

          <div className="space-y-6">
            {/* Current Plan */}
            <div className="bg-muted/30 border border-border rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-[14px] font-medium mb-1">
                    {planDisplayName || 'Plan Darmowy'}
                  </h3>
                  <p className="text-[12px] text-muted-foreground">
                    {creditsAvailable} / {creditsLimit === 0 ? '∞' : creditsLimit} kredytów dostępnych
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-[18px] font-semibold">
                    {priceDisplay}
                  </div>
                </div>
              </div>

              {/* Credits Progress Bar */}
              {creditsLimit > 0 && (
                <div className="mb-4">
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${Math.min((creditsAvailable / creditsLimit) * 100, 100)}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Wykorzystano {creditsLimit - creditsAvailable} z {creditsLimit} kredytów w tym miesiącu
                  </p>
                </div>
              )}

              {/* Renewal Info */}
              {subscription?.renews_at && (
                <p className="text-[11px] text-muted-foreground mb-4">
                  Odnawia się: {new Date(subscription.renews_at).toLocaleDateString('pl-PL')}
                </p>
              )}

              <div className="flex gap-3">
                <Button variant="default" size="sm" onClick={() => setIsPricingOpen(true)}>
                  Zmień plan
                </Button>
                {subscription?.lemon_squeezy_customer_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open('https://app.lemonsqueezy.com/my-orders', '_blank')}
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                    Zarządzaj płatnościami
                  </Button>
                )}
              </div>
            </div>

            {/* Pricing Dialog */}
            <PricingDialog open={isPricingOpen} onOpenChange={setIsPricingOpen} />

            {/* NOTE: Billing Info and Invoice History sections are temporarily hidden
                Uncomment when invoice functionality is implemented */}
          </div>
        </section>

        {/* Branding & Watermarks */}
        <section className="mb-12">
          <h2 className="text-[15px] font-medium mb-6 pb-3 border-b border-border">
            Branding i Znaki Wodne
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="space-y-8">
              {/* Agency Logo Watermark */}
              <div className="border border-border rounded-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="font-medium text-[13px]">Logo Agencji</Label>
                    <p className="text-[11px] text-muted-foreground">
                      Dodaj Twoje logo do wizualizacji
                    </p>
                  </div>
                  <Switch
                    checked={watermarkEnabled}
                    onCheckedChange={setWatermarkEnabled}
                  />
                </div>

                {watermarkEnabled && (
                  <div className="space-y-4 pt-4 border-t border-border/50">
                    {/* Logo Upload */}
                    <div>
                      <Label className="text-[12px] mb-2 block">Logo (PNG/JPG, max 2MB)</Label>
                      <div className="flex items-center gap-3">
                        {settings?.watermark_logo_url && (
                          <img
                            src={settings.watermark_logo_url}
                            alt="Logo"
                            className="h-10 w-auto object-contain rounded border"
                          />
                        )}
                        <Input
                          type="file"
                          className="hidden"
                          id="logo-upload"
                          accept="image/png, image/jpeg"
                          onChange={handleLogoUpload}
                          disabled={uploadingLogo}
                        />
                        <Button variant="outline" size="sm" asChild>
                          <label htmlFor="logo-upload" className="cursor-pointer">
                            {uploadingLogo ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                            ) : (
                              <Upload className="w-3.5 h-3.5 mr-1.5" />
                            )}
                            {settings?.watermark_logo_url ? 'Zmień' : 'Wgraj logo'}
                          </label>
                        </Button>
                      </div>
                    </div>

                    {/* Position Dropdown */}
                    <div>
                      <Label className="text-[12px] mb-2 block">Pozycja</Label>
                      <Select value={watermarkPosition} onValueChange={setWatermarkPosition}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="top-left">Lewy górny róg</SelectItem>
                          <SelectItem value="top-right">Prawy górny róg</SelectItem>
                          <SelectItem value="bottom-left">Lewy dolny róg</SelectItem>
                          <SelectItem value="bottom-right">Prawy dolny róg</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Opacity Slider */}
                    <div>
                      <div className="flex justify-between mb-2">
                        <Label className="text-[12px]">Przezroczystość</Label>
                        <span className="text-[11px] text-muted-foreground">{watermarkOpacity}%</span>
                      </div>
                      <Slider
                        value={[watermarkOpacity]}
                        onValueChange={([v]) => setWatermarkOpacity(v)}
                        min={10}
                        max={100}
                        step={5}
                      />
                    </div>

                    {/* Scale Slider */}
                    <div>
                      <div className="flex justify-between mb-2">
                        <Label className="text-[12px]">Rozmiar</Label>
                        <span className="text-[11px] text-muted-foreground">{watermarkScale}%</span>
                      </div>
                      <Slider
                        value={[watermarkScale]}
                        onValueChange={([v]) => setWatermarkScale(v)}
                        min={50}
                        max={200}
                        step={10}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Wysko Watermark */}
              <div className="border border-border rounded-lg p-5">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="font-medium text-[13px]">Znak wodny Wysko</Label>
                    <p className="text-[11px] text-muted-foreground">
                      Subtelne oznaczenie "Created with Wysko"
                    </p>
                  </div>
                  <Switch
                    checked={wyskoWatermarkEnabled}
                    onCheckedChange={setWyskoWatermarkEnabled}
                  />
                </div>

                {wyskoWatermarkEnabled && (
                  <div className="pt-4 border-t border-border/50 space-y-4">
                    {/* Pozycja */}
                    <div>
                      <Label className="text-[12px] mb-2 block">Pozycja</Label>
                      <Select value={wyskoWatermarkPosition} onValueChange={setWyskoWatermarkPosition}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="top-left">Lewy górny róg</SelectItem>
                          <SelectItem value="top-right">Prawy górny róg</SelectItem>
                          <SelectItem value="bottom-left">Lewy dolny róg</SelectItem>
                          <SelectItem value="bottom-right">Prawy dolny róg</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Przezroczystość */}
                    <div>
                      <Label className="text-[12px] mb-2 block">Przezroczystość: {wyskoWatermarkOpacity}%</Label>
                      <Slider
                        value={[wyskoWatermarkOpacity]}
                        onValueChange={([v]) => setWyskoWatermarkOpacity(v)}
                        min={10}
                        max={100}
                        step={5}
                      />
                    </div>

                    {/* Rozmiar */}
                    <div>
                      <Label className="text-[12px] mb-2 block">Rozmiar: {wyskoWatermarkScale}%</Label>
                      <Slider
                        value={[wyskoWatermarkScale]}
                        onValueChange={([v]) => setWyskoWatermarkScale(v)}
                        min={50}
                        max={200}
                        step={10}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Preview */}
            <div>
              <Label className="text-[13px] mb-3 block">Podgląd znaku wodnego</Label>
              <WatermarkPreview
                watermarkEnabled={watermarkEnabled}
                watermarkPosition={watermarkPosition}
                watermarkOpacity={watermarkOpacity}
                watermarkScale={watermarkScale}
                watermarkLogoUrl={settings?.watermark_logo_url}
                wyskoWatermarkEnabled={wyskoWatermarkEnabled}
                wyskoWatermarkPosition={wyskoWatermarkPosition}
                wyskoWatermarkOpacity={wyskoWatermarkOpacity}
                wyskoWatermarkScale={wyskoWatermarkScale}
                wyskoLogoUrl={wyskoLogoUrl}
                sampleImageUrl={sampleImageUrl}
                isLoadingSample={isLoadingSample}
              />
            </div>
          </div>

          {/* Save Button for Branding */}
          <div className="mt-6 pt-6 border-t border-border flex justify-end">
            <Button onClick={handleSaveAll}>
              <Check className="w-4 h-4 mr-2" />
              Zapisz ustawienia znaku wodnego
            </Button>
          </div>
        </section>

        {/* Preferencje Renderowania (Defaults) */}
        <section className="mb-12">
          <h2 className="text-[15px] font-medium mb-6 pb-3 border-b border-border">
            Preferencje Renderowania
          </h2>
          <div className="space-y-6 text-sm text-muted-foreground">
            {/* Simplified for now as focus was on Branding/Billing */}
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4" />
              Dodatkowe ustawienia renderowania będą dostępne wkrótce.
            </div>
          </div>
        </section>

        {/* Admin Panel - tylko dla adminów */}
        {onOpenAdmin && (
          <section className="mb-12">
            <h2 className="text-[15px] font-medium mb-6 pb-3 border-b border-border">
              Panel Administracyjny
            </h2>
            <div className="space-y-4">
              <div className="bg-muted/30 border border-border rounded-lg p-6">
                <h3 className="text-[14px] font-medium mb-2">Panel Administratora</h3>
                <p className="text-[12px] text-muted-foreground mb-4">
                  Zarządzaj promptami AI, użytkownikami, mailingiem i ustawieniami aplikacji.
                </p>
                <Button variant="outline" size="sm" onClick={onOpenAdmin}>
                  Otwórz panel administratora
                </Button>
              </div>
            </div>
          </section>
        )}

        {/* Delete Account Dialog */}
        <DeleteAccountDialog
          open={isDeleteAccountOpen}
          onOpenChange={setIsDeleteAccountOpen}
        />

      </div>
    </div>
  );
}
